// SeriesProgressStrip — a dot strip representing one recurring series,
// reusing the milestone progress-dot colour vocabulary (see the
// milestone ProgressDots component): completed-on-time = filled
// emerald, completed-late = filled amber, current = filled brand-500
// with a white centre pip, future = outlined slate (transparent fill).
// The current-occurrence pip renders ONLY on the single 'current' dot.
// Sourced from ActivityDetail.detail.seriesNeighbours (present only
// when the activity is recurring); the caller passes the per-occurrence
// states + the human caption.

export type SeriesDotState =
  | "completed-on-time"
  | "completed-late"
  | "current"
  | "future";

interface SeriesProgressStripProps {
  occurrences: { state: SeriesDotState }[];
  caption: string;
}

// Milestone progress-dot vocabulary. The class strings carry the
// colour-family substrings (emerald / amber / slate / brand) the
// committed test greps for, and keep the values single-sourced to the
// established milestone palette.
const DOT_CLASS: Record<SeriesDotState, string> = {
  "completed-on-time": "bg-emerald-500 border border-emerald-500",
  "completed-late": "bg-amber-500 border border-amber-500",
  current: "bg-brand-500 border border-brand-500",
  future: "border border-slate-400 bg-transparent dark:border-slate-500",
};

export default function SeriesProgressStrip({
  occurrences,
  caption,
}: SeriesProgressStripProps) {
  return (
    <div className="space-y-1.5">
      <div
        role="img"
        aria-label={caption}
        className="flex flex-wrap items-center gap-1"
      >
        {occurrences.map((occ, i) => (
          <span
            key={i}
            data-dot-state={occ.state}
            className={`relative inline-flex h-2.5 w-2.5 items-center justify-center rounded-full ${DOT_CLASS[occ.state]}`}
          >
            {occ.state === "current" && (
              <span
                data-current-pip="true"
                aria-hidden="true"
                className="block h-1 w-1 rounded-full bg-white"
              />
            )}
          </span>
        ))}
      </div>
      <p className="text-caption text-slate-500 dark:text-slate-400">
        {caption}
      </p>
    </div>
  );
}
